import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiClient } from "../utils/apiClient";
import type {
  DobbyAiRule,
  RulesFetchOrgExampleResponse,
  RulesFetchResponse,
  RulesFetchBody,
  RulesUpdateResponse,
  RulesUpdateBody
} from "@services/documents/rules/types";

interface ControlCenterStore {
  rules: {
    [key: string]: DobbyAiRule;
  };
  loading: boolean;
  error: string | null;
}

export const initialState: ControlCenterStore = {
  rules: {},
  loading: false,
  error: null,
};

export const fetchOrgExampleRule = createAsyncThunk(
  "controlCenterData/fetchOrgExampleRule",
  async () => {
    const response =
      await apiClient.post<RulesFetchOrgExampleResponse>(
        "/documents/rules/fetch-org-example/",
        {}
      );
    return response.rule;
  }
);

export const refreshRule = createAsyncThunk(
  "controlCenterData/refreshRule",
  async (ruleId: string) => {
    const response = await apiClient.post<RulesFetchResponse>(
      "/documents/rules/fetch/",
      { ruleId } satisfies RulesFetchBody
    );
    const rule = response.rules.find(
      (r: any) => r._id === ruleId || r.id === ruleId
    );
    if (!rule) {
      throw new Error("Rule not found");
    }
    return { ruleId, rule };
  }
);

export const updateRuleContent = createAsyncThunk(
  "controlCenterData/updateRuleContent",
  async ({ ruleId, content }: { ruleId: string; content: string }) => {
    const response = await apiClient.post<RulesUpdateResponse>(
      "/documents/rules/update/",
      { ruleId: ruleId, content } satisfies RulesUpdateBody
    );
    return response.updatedRule;
  }
);

const slice = createSlice({
  name: "controlCenterData",
  initialState,
  reducers: {
    setRuleData: (
      state,
      action: PayloadAction<{ ruleId: string; rule: DobbyAiRule }>
    ) => {
      state.rules[action.payload.ruleId] = action.payload.rule;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgExampleRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrgExampleRule.fulfilled, (state, action) => {
        state.loading = false;
        const rule = action.payload as any;
        const ruleId = rule._id || rule.id;
        state.rules[ruleId] = rule;
      })
      .addCase(fetchOrgExampleRule.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch org example rule";
      })
      .addCase(refreshRule.fulfilled, (state, action) => {
        const { ruleId, rule } = action.payload;
        state.rules[ruleId] = rule;
      })
      .addCase(updateRuleContent.fulfilled, (state, action) => {
        const rule = action.payload as any;
        const ruleId = rule._id || rule.id;
        state.rules[ruleId] = rule;
      });
  },
});

export const { setRuleData } = slice.actions;

export default slice.reducer;
